import { HeroImage } from "@/components/auth/hero-image";
import { OnboardingPage } from "@/components/auth/onboarding-page";
import { onError } from "@/components/core/display-error";
import { Link, LinkButton } from "@/components/core/tanstack-link";
import { EmailOauth } from "@/components/email-settings/email-oauth";
import { EmailComplete } from "@/components/email-settings/email-complete";
import { EmailCreate } from "@/components/email-settings/email-create";
import { queryClient } from "@/helpers/tanstack-client";
import {
  getEmails,
  getGetEmailsQueryKey,
  useGetEmails,
  Provider as ProviderType,
  EmailSettingsOut,
  EmailSettingsStatus,
  sendVerifyEmail,
  Provider,
  updateEmailSettings,
  useGetVerifiedDomains,
} from "@/models/backend";
import {
  Alert,
  AlertTitle,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import z from "zod";

const searchSchema = z.object({
  page: z.enum(["setup", "provider", "finish"]).optional().catch("setup"),
  settingsId: z.uuid().optional().catch(undefined),
  oauth_error: z.string().optional().catch(undefined),
  error_code: z.string().optional().catch(undefined),
  error_subcode: z.string().optional().catch(undefined),
  provider: z.custom<ProviderType>().optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/onboarding/email")({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ page: search.page, settingsId: search.settingsId }),
  loader: async ({ deps: { page, settingsId } }) => {
    const data = await queryClient.fetchQuery({
      queryFn: (opts) => getEmails(undefined, opts),
      queryKey: getGetEmailsQueryKey(),
    });
    if (!page) {
      if (data.length) {
        throw redirect({ to: "/dashboard" });
      }
      throw redirect({ search: { page: "setup" } });
    }
    return { isOnboarding: data.filter((setting) => setting.id != settingsId).length === 0 };
  },
});

const Setup = ({
  isOnboarding,
  onShareSendingChange,
}: {
  isOnboarding: boolean;
  onShareSendingChange: (shareSending: boolean) => void;
}) => {
  return (
    <>
      <Stack spacing={1}>
        <Typography variant="h3">Add an email account</Typography>
        <Typography variant="body1" color="text.secondary">
          {isOnboarding
            ? "Use a company email to send reminders."
            : "Add an email address or a shared inbox."}
        </Typography>
        <EmailCreate isOnboarding={isOnboarding} onShareSendingChange={onShareSendingChange} />
      </Stack>
    </>
  );
};

const Permissions = () => {
  const search = Route.useSearch();
  const { data: emails } = useGetEmails();
  const { data: verifiedDomains } = useGetVerifiedDomains();
  if (!emails) {
    return null;
  }
  const settings = emails.find((e) => e.id === search.settingsId);
  const isVerified = verifiedDomains?.domains.some((d) => d === settings?.email.split("@")[1]);
  return (
    <>
      <Stack spacing={1}>
        <Typography variant="h3">Add an email account</Typography>
        <Typography variant="body1" color="text.secondary">
          Log in to send via this email. If{" "}
          {settings?.email ? (
            <MuiLink href={`mailto:${settings.email}`}>{settings.email}</MuiLink>
          ) : (
            "this address"
          )}{" "}
          is a shared inbox, log in to any account that can access the mailbox.
        </Typography>
      </Stack>
      <EmailOauth selectedTab={search.provider ?? Provider.google} isVerifiedDomain={isVerified} />
    </>
  );
};

const getError = ({
  search,
  settings,
}: {
  search: ReturnType<typeof Route.useSearch>;
  settings: EmailSettingsOut | undefined;
}) => {
  if (
    search.provider === ProviderType.microsoft &&
    search.error_code == "access_denied" &&
    search.error_subcode == "cancel"
  ) {
    const link = "https://trove.works/outlook-admin-consent/";
    return {
      error: (
        <Stack spacing={2} sx={{ alignItems: "start" }}>
          <Alert severity="error">
            <AlertTitle>You may need IT Admin permission.</AlertTitle>
            <Typography>
              Share this link with them to let them approve Trove on behalf of your organization:{" "}
              <MuiLink color="warning" href={link} target="_blank">
                {link}
              </MuiLink>
            </Typography>
            <Typography>Once they confirm they have approved, come back here.</Typography>
          </Alert>
        </Stack>
      ),
      cantSend: true,
    };
  }
  if (search.oauth_error) {
    return {
      error: (
        <Alert
          severity="error"
          action={
            <LinkButton
              to="/onboarding/email"
              search={{ ...search, page: "provider" }}
              color="error"
            >
              Try again
            </LinkButton>
          }
        >
          {search.oauth_error}
        </Alert>
      ),
      cantSend: true,
    };
  } else if (search.settingsId == null) {
    return {
      error: (
        <Alert
          severity="error"
          action={
            <LinkButton to="/onboarding/email" search={{ ...search, page: "setup" }} color="error">
              Start over
            </LinkButton>
          }
        >
          Can't find email to add
        </Alert>
      ),
      cantSend: true,
    };
  }
  if (!settings) {
    return {
      error: (
        <Alert
          severity="error"
          action={
            <LinkButton
              to="/onboarding/email"
              search={{ ...search, page: "provider" }}
              color="error"
            >
              Try again
            </LinkButton>
          }
        >
          Can't find email to add
        </Alert>
      ),
      cantSend: true,
    };
  }
  if (!settings?.email_account?.can_share_sending) {
    return {
      error: (
        <Alert
          severity="error"
          action={
            <LinkButton
              to="/onboarding/email"
              search={{ ...search, page: "provider" }}
              color="error"
            >
              Grant permissions
            </LinkButton>
          }
        >
          Permissions to send email not granted. Try again to grant
        </Alert>
      ),
      cantSend: true,
    };
  }
  if (!settings.email_account?.can_share_inbox) {
    return {
      error: (
        <Alert
          severity="warning"
          action={
            <LinkButton
              to="/onboarding/email"
              search={{ ...search, page: "provider" }}
              color="warning"
            >
              Grant permissions
            </LinkButton>
          }
        >
          Permissions to read not granted. Try again to grant
        </Alert>
      ),
      cantSend: false,
    };
  }
  return {
    error: null,
    cantSend: false,
  };
};

const Finish = ({ isOnboarding }: { isOnboarding: boolean }) => {
  const search = Route.useSearch();
  const [testEmail, setTestEmail] = useState<false | "todo" | "sending" | "success" | "error">(
    false,
  );
  const [sendError, setSendError] = useState<string | undefined>();
  const { data: emails } = useGetEmails();
  const { data: verifiedDomains } = useGetVerifiedDomains();

  useEffect(() => {
    const inner = async () => {
      if (testEmail === "todo") {
        setTestEmail("sending");
        try {
          await sendVerifyEmail({ id: search.settingsId ?? "" });
          setTestEmail("success");
        } catch (e) {
          if (isAxiosError(e)) {
            setSendError(e.response?.data.detail);
          }
          setTestEmail("error");
        }
      }
    };
    inner();
  }, [testEmail, search.settingsId]);
  if (!emails) {
    return null;
  }
  const settings = emails.find((e) => e.id === search.settingsId);
  const isVerified = verifiedDomains?.domains.some((d) => d === settings?.email.split("@")[1]);
  const anySending = emails.some((e) => e.status in [EmailSettingsStatus.success]);
  const { error, cantSend } = !isVerified
    ? getError({ search, settings })
    : { error: null, cantSend: false };
  const handleResetEmail = async () => {
    try {
      await updateEmailSettings({ id: settings?.id, email: settings?.email_account?.email });
      queryClient.invalidateQueries({ queryKey: getGetEmailsQueryKey() });
      setSendError(undefined);
      setTestEmail("todo");
    } catch (e) {
      onError({ prefix: "Failed to update sender email" })(e);
    }
  };

  switch (testEmail) {
    case false:
      if (error) {
        return (
          <EmailComplete
            isOnboarding={isOnboarding}
            anySending={anySending}
            error={error}
            cantSend={cantSend}
            emailSettings={settings}
          />
        );
      }
      setTestEmail("todo");
    case "todo":
    case "sending":
      return <CircularProgress />;
    case "success":
      return (
        <EmailComplete
          isOnboarding={isOnboarding}
          anySending={anySending}
          cantSend={false}
          emailSettings={settings}
        />
      );
    case "error":
      if (sendError == "MailboxNotEnabledForRESTAPI") {
        return (
          <EmailComplete
            anySending={anySending}
            emailSettings={settings}
            isOnboarding={isOnboarding}
            cantSend={true}
            error={
              <Alert severity="error">
                Couldn't send test email. The error returned was "The mailbox is either inactive,
                soft-deleted, or is hosted on-premise."
                <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: "end" }}>
                  <LinkButton
                    color="error"
                    variant="outlined"
                    to="."
                    search={{ ...search, page: "provider" }}
                  >
                    Log in as another user
                  </LinkButton>
                </Stack>
              </Alert>
            }
          />
        );
      }
      if (sendError == "ErrorSendAsDenied") {
        return (
          <EmailComplete
            anySending={anySending}
            emailSettings={settings}
            isOnboarding={isOnboarding}
            cantSend={true}
            error={
              <Alert severity="error">
                Couldn't send test email. You're logged in as {settings?.email_account?.email}, but
                that account doesn't have permission to send as {settings?.email}.
                <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: "end" }}>
                  <Button color="error" variant="outlined" onClick={handleResetEmail}>
                    Send as{" "}
                    {settings?.email_account?.email.slice(
                      0,
                      settings?.email_account?.email.indexOf("@") + 1,
                    )}
                    ... instead
                  </Button>

                  <LinkButton
                    color="error"
                    variant="outlined"
                    to="."
                    search={{ ...search, page: "provider" }}
                  >
                    Log in as another user
                  </LinkButton>
                </Stack>
              </Alert>
            }
          />
        );
      }
      return (
        <EmailComplete
          anySending={anySending}
          emailSettings={settings}
          isOnboarding={isOnboarding}
          cantSend={true}
          error={
            <Alert severity="error">
              Couldn't send test email. Contact <a href="mailto:hi@trove.works">hi@trove.works</a>{" "}
              for support.
            </Alert>
          }
        />
      );
    default:
      return (
        <EmailComplete
          isOnboarding={isOnboarding}
          cantSend={false}
          anySending={anySending}
          emailSettings={settings}
        />
      );
  }
};

function RouteComponent() {
  const { isOnboarding } = Route.useLoaderData();
  const { page, ...search } = Route.useSearch();
  const [isSendFrom, setIsSendFrom] = useState(true);
  const showRemainingSteps = page !== "setup" || isSendFrom;
  return (
    <OnboardingPage
      page={
        <Stack spacing={3}>
          {isOnboarding && (
            <Breadcrumbs separator=">">
              <Link to="/onboarding/about-you">About you</Link>
              <Link to="/onboarding/invoices">Invoices</Link>
              <Typography>Email</Typography>
            </Breadcrumbs>
          )}
          <Breadcrumbs separator=">">
            {page !== "setup" ? (
              <Link to="." search={{ page: "setup", ...search }}>
                Add email
              </Link>
            ) : (
              <Typography>Add email</Typography>
            )}
            {showRemainingSteps &&
              (page !== "provider" ? (
                <Link to="." search={{ page: "provider", ...search }}>
                  Set permissions
                </Link>
              ) : (
                <Typography>Set permissions</Typography>
              ))}
            {showRemainingSteps &&
              (page !== "finish" ? (
                <Link to="." search={{ page: "finish", ...search }}>
                  Test sending
                </Link>
              ) : (
                <Typography>Test sending</Typography>
              ))}
          </Breadcrumbs>
          {page === "setup" && (
            <Setup isOnboarding={isOnboarding} onShareSendingChange={setIsSendFrom} />
          )}
          {page === "provider" && <Permissions />}
          {page === "finish" && <Finish isOnboarding={isOnboarding} />}
        </Stack>
      }
      hero={
        <HeroImage
          url="/assets/onboarding-4.svg"
          copy="Send reminders via your email address"
          imageMarginTop={4}
        />
      }
    />
  );
}
